/**
 * A JOIN experiment's offer (`?eid=`), as data: an ad's link names an experiment, marketing-kapi's
 * public lookup says what it offers, and the flow becomes that offer: its home studio (no city to
 * choose), its plans and prices (patient-kapi prices them for the eid), its headline in the strip
 * and over the plans. An eid that is off, unknown or gone is the regular flow, with a note that
 * the offer has ended.
 *
 * Pure, and part of every page's first load (startPrefetch.js asks for the lookup), so it stays
 * small and imports nothing.
 */

// Longer than any id. A link is anyone's to write, and the id goes in the lookup's path.
const EXPERIMENT_ID_MAX_LENGTH = 128

/** A link's eid, trimmed, or null when there's none (or it's longer than any id). */
export const normalizeExperimentId = (value) => {
  const id = typeof value === 'string' ? value.trim() : ''
  return id && id.length <= EXPERIMENT_ID_MAX_LENGTH ? id : null
}

/** The eid in a query string ("a=1&eid=…", with or without its "?"), normalised. */
export const experimentIdOf = (search) => normalizeExperimentId(new URLSearchParams(search || '').get('eid'))

/** The public lookup's answer, which doesn't change over a visit. */
export const experimentQueryKey = (experimentId) => ['start', 'experiment', experimentId]
export const EXPERIMENT_STALE_MS = 10 * 60 * 1000
export const EXPERIMENT_RETRIES = 1

/** Retried as a campaign's copy is, except an unknown id: a 404 says the same thing again. */
export const experimentRetry = (count, error) => error?.status !== 404 && count < EXPERIMENT_RETRIES

export const OfferStatus = {
  // No eid: the regular flow, untouched.
  NONE: 'NONE',
  // The lookup hasn't answered: the steps that depend on it wait (the city, the prices).
  LOADING: 'LOADING',
  ACTIVE: 'ACTIVE',
  // Off, unknown (404), not a JOIN experiment, or without a home studio: the regular flow, and
  // the strip says the offer has ended.
  ENDED: 'ENDED',
  // The lookup failed (a network error, a 500): the regular flow, and the strip says we couldn't
  // load the offer, since we don't know that it ended. patient-kapi, which asks the same lookup,
  // treats it as no experiment too.
  UNAVAILABLE: 'UNAVAILABLE'
}

const text = (value) => (typeof value === 'string' && value.trim() ? value.trim() : null)

/**
 * The offer an experiment makes, from the public lookup's body, or null when it makes none: only a
 * JOIN experiment that is on and has a home studio does (as patient-kapi decides it). Every field
 * but the home studio may be missing; the flow falls back to its own words and the regular plans.
 */
export const offerFrom = (experiment, experimentId) => {
  const config = experiment?.config || {}
  if (experiment?.experimentType !== 'JOIN' || experiment.isEnabled !== true) return null
  const homeLocationId = Number(config.homeLocationId)
  if (!Number.isInteger(homeLocationId) || homeLocationId <= 0) return null
  return {
    // The link's own id is what prep and analytics send: it is the one patient-kapi looks up.
    experimentId,
    homeLocationId,
    headline: text(config.offerHeadline),
    details: text(config.offerDetails),
    // A deposit: its first charge is all the plan step names ("$20 deposit"), with no price after
    // it. Missing (an older marketing-kapi) is no deposit.
    isDeposit: config.isDeposit === true
  }
}

/**
 * Where the offer stands, from the eid and the lookup's query. A 404 newer than the answer kept
 * from before (the experiment deleted since) speaks over it.
 */
export const offerStatus = ({ experimentId, query }) => {
  if (!experimentId) return OfferStatus.NONE
  if (query.isError && query.error?.status === 404 && (query.errorUpdatedAt ?? 0) >= (query.dataUpdatedAt ?? 0)) return OfferStatus.ENDED
  if (query.data) return offerFrom(query.data, experimentId) ? OfferStatus.ACTIVE : OfferStatus.ENDED
  if (query.isError) return query.error?.status === 404 ? OfferStatus.ENDED : OfferStatus.UNAVAILABLE
  return OfferStatus.LOADING
}

/**
 * Whether a studio can take an offer's member, as patient-kapi checks it: on, and not for home
 * members only. Unlike public signup it needn't be listed for signup (`isJoinFlowVisible`): a
 * pre-open studio is hidden from the city list but reached through its experiment's link.
 */
export const isOfferStudio = (location) => (
  location?.id > 0 && location.isActive === true && location.isHomeMembersOnly === false
)

/**
 * The offer's home studio, `{ geoId, location }`, from every city and studio as public-geos lists
 * them (not only those listed for signup), or null when none can take its member.
 */
export const offerStudio = ({ geos, offer }) => {
  if (!offer) return null
  for (const geo of geos || []) {
    const location = (geo.locations || []).find(it => it.id === offer.homeLocationId)
    if (location) return isOfferStudio(location) ? { geoId: location.geoId ?? geo.id, location } : null
  }
  return null
}

/** The city holding the offer's home studio; null if no city has it, or it can't take members. */
export const offerGeoId = ({ geos, offer }) => offerStudio({ geos, offer })?.geoId ?? null

/** The strip's line for an offer, `{ bold, rest }`; null when the offer has nothing to say. */
export const offerLine = ({ status, offer }) => {
  if (status === OfferStatus.ACTIVE) return offer?.headline ? { bold: offer.headline, rest: '' } : null
  if (status === OfferStatus.ENDED) return { bold: '', rest: 'This offer has ended' }
  if (status === OfferStatus.UNAVAILABLE) return { bold: '', rest: 'We couldn’t load this offer, so you’ll see our regular prices' }
  return null
}
